<template>
    <b-modal
        v-model="innerVisible"
        :title="modalTitle"
        size="sm"
        centered
        modal-class="factor-modal-shell"
        dialog-class="factor-modal-dialog"
        content-class="factor-modal-content"
        header-class="factor-modal-header"
        body-class="factor-modal-body"
        footer-class="factor-modal-footer"
        ok-title="저장"
        cancel-title="취소"
        ok-variant="primary"
        cancel-variant="secondary"
        :ok-disabled="saveDisabled"
        :busy="saving"
        :cancel-disabled="saving"
        @hide="onHide"
        @ok="onModalOk"
    >
        <div v-if="isEdit && loading" class="text-muted py-3">불러오는 중…</div>
        <form v-else class="modal-form-content registration-form factor-modal-form" @submit.prevent="onFormSubmit">
            <div :class="['registration-unified', { 'registration-unified--create': isCreate }]">
                <div class="form-section form-section-basic">
                    <div class="section-header section-header-pill section-header-green">
                        <span class="section-pill section-pill-gray" aria-hidden="true"></span>
                        <h6>기본 정보</h6>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <div class="form-group">
                                <label>표시명</label>
                                <input
                                    v-model="form.factorName"
                                    type="text"
                                    class="form-control"
                                    maxlength="100"
                                    placeholder="예: 2024년 한국 LNG 배출계수 (선택)"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>배출계수 유형</label>
                                <select v-model="form.factorType" class="form-control">
                                    <option v-for="opt in factorTypeOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>연료 유형 <span class="required">*</span></label>
                                <select
                                    v-model="form.fuelType"
                                    class="form-control"
                                    required
                                    @change="onFuelOrCountryChange"
                                >
                                    <option value="">선택하세요</option>
                                    <option v-for="opt in fuelTypeOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>국가/출처 <span class="required">*</span></label>
                                <select
                                    v-model="form.country"
                                    class="form-control"
                                    required
                                    @change="onFuelOrCountryChange"
                                >
                                    <option value="">선택하세요</option>
                                    <option v-for="opt in regionOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>상태</label>
                                <select v-model="form.draftStat" class="form-control">
                                    <option v-for="opt in draftStats" :key="opt.value" :value="opt.value">
                                        {{ opt.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <FactorValueFormBody
                    :key="'vf-' + mode + '-' + valueFormKey"
                    :mode="mode"
                    :gas-emission-unit-options="gasEmissionUnitOptions"
                    :factor-source-options="factorSourceOptions"
                />

                <div
                    v-if="String(form.factorType || 'FUEL').toUpperCase() === 'FUEL'"
                    class="form-section form-section-calorific"
                >
                    <div class="section-header section-header-pill section-header-muted">
                        <span class="section-pill section-pill-gray" aria-hidden="true"></span>
                        <h6>열량계수</h6>
                        <span class="optional-chip optional-chip--section">선택 입력</span>
                    </div>
                    <p class="calorific-intro text-muted">
                        연료 사용량을 에너지 기준으로 환산할 때만 사용하며, 미입력 상태로도 저장할 수 있습니다.
                    </p>
                    <div class="calorific-cols">
                        <div class="calorific-col">
                            <label class="calorific-label">순발열량 (NCV)</label>
                            <div class="input-group calorific-input-group">
                                <input
                                    v-model.number="form.ncvValue"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="값 (선택)"
                                />
                                <select v-model="form.ncvUnit" class="form-control calorific-unit-select">
                                    <option value="">단위 선택</option>
                                    <option v-for="opt in calorificUnitOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.value }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="calorific-col">
                            <label class="calorific-label">총발열량 (GCV)</label>
                            <div class="input-group calorific-input-group">
                                <input
                                    v-model.number="form.gcvValue"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="값 (선택)"
                                />
                                <select v-model="form.gcvUnit" class="form-control calorific-unit-select">
                                    <option value="">단위 선택</option>
                                    <option v-for="opt in calorificUnitOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.value }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-section form-section-remarks">
                <div class="section-header section-header-pill section-header-gray">
                    <span class="section-pill section-pill-gray" aria-hidden="true"></span>
                    <h6>비고</h6>
                </div>
                <p class="section-brief">{{ remarksSectionBrief }}</p>
                <div class="form-group">
                    <textarea
                        v-model="form.description"
                        class="form-control"
                        rows="2"
                        placeholder="2024년 한국 환경부 고시값 반영"
                    ></textarea>
                </div>
            </div>

            <div v-if="isCreate && autoGeneratedFactorCode" class="registration-code-meta">
                <div class="footer-factorcode" title="국가/출처·유형·시작월 기준 자동 생성">
                    <span class="footer-factorcode-label">배출계수 코드</span>
                    <span class="footer-factorcode-value">{{ autoGeneratedFactorCode }}</span>
                    <span
                        v-if="factorCodeCheckStatus === 'available'"
                        class="footer-factorcode-status footer-factorcode-status--available"
                        >· 사용 가능</span
                    >
                    <span
                        v-else-if="factorCodeCheckStatus === 'duplicate'"
                        class="footer-factorcode-badge footer-factorcode-badge--duplicate"
                        title="이 배출계수 코드는 이미 등록되어 있습니다. 국가/출처, 배출계수 유형, 시작월을 변경해주세요."
                        >이미 사용 중</span
                    >
                    <span
                        v-else-if="factorCodeCheckStatus === 'checking'"
                        class="footer-factorcode-status footer-factorcode-status--checking"
                        >· 확인 중</span
                    >
                </div>
            </div>
            <div v-else-if="isEdit && form.factorCode" class="registration-code-meta">
                <div class="footer-factorcode">
                    <span class="footer-factorcode-label">배출계수 코드</span>
                    <span class="footer-factorcode-value">{{ form.factorCode }}</span>
                </div>
            </div>
        </form>
    </b-modal>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "../../../codes";
import FactorValueFormBody from "./FactorValueFormBody.vue";
import {
    buildAllComponents,
    validateUniqueGasComponentCodes,
    basketPositiveTotals,
    splitSpeciesRowsFromComponents,
    getCalorificFromComponents,
} from "@/submodules/carbon-plugin/utils/factorComponentHelper";

function emptySpeciesRows() {
    return [{ componentCode: "", emissionFactor: null }];
}

function emptyFactorFormBase() {
    return {
        factorIdx: null,
        factorCode: "",
        valueIdx: null,
        factorName: "",
        country: "KR",
        fuelType: "",
        factorType: "FUEL",
        startMonth: "",
        endMonth: "",
        source: "",
        draftStat: "DRAFT",
        description: "",
        gasEmissionUnit: "kgGHG/TJ",
        CO2Factor: null,
        CH4Factor: 0,
        N2OFactor: 0,
        SF6Factor: 0,
        hfcSpeciesRows: emptySpeciesRows(),
        pfcSpeciesRows: emptySpeciesRows(),
        detailBasisHFCs: "",
        detailBasisPFCs: "",
        ncvValue: null,
        ncvUnit: "",
        gcvValue: null,
        gcvUnit: "",
    };
}

function getEmptyFormForCreate() {
    const now = new Date();
    const startMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
    return { ...emptyFactorFormBase(), startMonth };
}

export default {
    name: "FactorFormModal",
    components: { FactorValueFormBody },
    props: {
        mode: {
            type: String,
            required: true,
            validator: (v) => v === "create" || v === "edit",
        },
        visible: { type: Boolean, default: false },
        factors: { type: Array, default: () => [] },
        factorToEdit: { type: Object, default: null },
    },
    data() {
        return {
            form: emptyFactorFormBase(),
            factorCodeCheckStatus: null,
            factorCodeDebounceTimer: null,
            saving: false,
            loading: false,
            valueFormKey: 0,
        };
    },
    provide() {
        return {
            carbonFactorFormHost: this,
        };
    },
    computed: {
        isCreate() {
            return this.mode === "create";
        },
        isEdit() {
            return this.mode === "edit";
        },
        modalTitle() {
            return this.isCreate ? "신규 배출계수 등록" : "배출계수 수정";
        },

        remarksSectionBrief() {
            return this.isCreate
                ? "기준 문서, 산정 조건 등 추후 검증에 필요한 메모를 남겨주세요."
                : "수정 근거, 산정 전제 등 변경 이력을 간단히 남겨주세요.";
        },
        innerVisible: {
            get() {
                return this.visible;
            },
            set(v) {
                this.$emit("update:visible", v);
            },
        },
        draftStats() {
            return CarbonCodes.draftStat.getList();
        },
        fuelTypeOptions() {
            return CarbonCodes.fuelType.getList();
        },
        factorTypeOptions() {
            const list = CarbonCodes.factorType?.getList?.() ?? [];
            if (Array.isArray(list) && list.length > 0) return list;
            return [
                { value: "FUEL", text: "FUEL" },
                { value: "ACTIVITY", text: "ACTIVITY" },
            ];
        },
        calorificUnitOptions() {
            const list = CarbonCodes.calorificUnit?.getList?.() ?? [];
            return Array.isArray(list) ? list : [];
        },
        regionOptions() {
            return [
                { value: "KR", text: "한국" },
                { value: "EU", text: "유럽" },
            ];
        },
        factorSourceOptions() {
            const list = CarbonCodes.factorSource?.getList?.() ?? [];
            return Array.isArray(list) ? list : [];
        },
        gasEmissionUnitOptions() {
            const activityUnits = CarbonCodes.activityUnit.getList();
            if (!Array.isArray(activityUnits) || activityUnits.length === 0) {
                return [
                    { value: "kgGHG/TJ", text: "kgGHG/TJ" },
                    { value: "kgGHG/kg", text: "kgGHG/kg" },
                ];
            }
            return activityUnits
                .map((item) => {
                    const denom = String(item?.value ?? "").trim();
                    if (!denom) return null;
                    const value = `kgGHG/${denom}`;
                    return { value, text: value };
                })
                .filter(Boolean);
        },
        autoGeneratedFactorCode() {
            const { country, fuelType, startMonth } = this.form;
            if (!country || !fuelType) return "";
            const parts = [country, fuelType];
            if (startMonth && String(startMonth).length === 6) parts.push(startMonth);
            const code = parts.join("-");
            return code
                .toUpperCase()
                .replace(/\s+/g, "-")
                .replace(/[^A-Z0-9-]/g, "");
        },
        saveDisabled() {
            if (this.isEdit) {
                return (
                    this.loading ||
                    this.saving ||
                    this.form.CO2Factor == null ||
                    this.form.CO2Factor === "" ||
                    this.form.factorIdx == null
                );
            }
            return (
                !this.autoGeneratedFactorCode ||
                this.factorCodeCheckStatus === "duplicate" ||
                !this.form.startMonth ||
                String(this.form.startMonth).length !== 6 ||
                this.form.CO2Factor == null ||
                this.form.CO2Factor === "" ||
                this.saving
            );
        },
    },
    watch: {
        visible(val) {
            if (!val) return;
            if (this.isCreate) {
                this.form = getEmptyFormForCreate();
                this.valueFormKey += 1;
                this.factorCodeCheckStatus = null;
                this.$nextTick(() => this.scheduleCodeCheck());
            } else if (this.factorToEdit?.factorIdx != null) {
                this.loadFactor();
            }
        },
        factorToEdit: {
            handler(next) {
                if (this.isEdit && this.visible && next?.factorIdx != null) {
                    this.loadFactor();
                }
            },
            deep: true,
        },
        "form.factorType"(next) {
            this.$nextTick(() => {
                const opts = this.gasEmissionUnitOptions;
                if (opts && opts.length > 0) {
                    this.form.gasEmissionUnit = opts[0].value;
                }
            });
            const t = String(next || "FUEL").toUpperCase();
            if (t !== "FUEL") {
                this.form.ncvValue = null;
                this.form.ncvUnit = "";
                this.form.gcvValue = null;
                this.form.gcvUnit = "";
            }
        },
        autoGeneratedFactorCode() {
            if (this.isCreate) this.scheduleCodeCheck();
        },
    },
    methods: {
        onStartMonthChange() {
            if (this.isCreate) this.scheduleCodeCheck();
        },
        onFuelOrCountryChange() {
            if (this.isCreate) this.scheduleCodeCheck();
        },
        scheduleCodeCheck() {
            if (!this.isCreate) return;
            if (this.factorCodeDebounceTimer) {
                clearTimeout(this.factorCodeDebounceTimer);
                this.factorCodeDebounceTimer = null;
            }
            if (this.autoGeneratedFactorCode && this.visible) {
                this.factorCodeDebounceTimer = setTimeout(() => {
                    this.checkFactorCodeDuplicate();
                    this.factorCodeDebounceTimer = null;
                }, 300);
            } else {
                this.factorCodeCheckStatus = null;
            }
        },
        checkFactorCodeDuplicate() {
            if (!this.autoGeneratedFactorCode) return;
            this.factorCodeCheckStatus = "checking";
            try {
                const isDuplicate = this.factors.some((f) => f.factorCode === this.autoGeneratedFactorCode);
                this.factorCodeCheckStatus = isDuplicate ? "duplicate" : "available";
            } catch {
                this.factorCodeCheckStatus = "available";
            }
        },
        onHide() {
            if (this.factorCodeDebounceTimer) {
                clearTimeout(this.factorCodeDebounceTimer);
                this.factorCodeDebounceTimer = null;
            }
            if (this.isCreate) this.factorCodeCheckStatus = null;
        },
        onFormSubmit() {
            if (this.saveDisabled) return;
            this.submit();
        },
        onModalOk(bvModalEvt) {
            if (bvModalEvt && typeof bvModalEvt.preventDefault === "function") {
                bvModalEvt.preventDefault();
            }
            if (this.saveDisabled) return;
            this.submit();
        },
        _currentYYYYMM() {
            const now = new Date();
            return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
        },
        async loadFactor() {
            const factorIdx = this.factorToEdit?.factorIdx;
            if (factorIdx == null) return;
            this.loading = true;
            try {
                const res = await carbonApi.emissionFactor.getFactorById(factorIdx);
                const data = res.data?.data || res.data;
                const full = data?.factor;
                const values = full?.factorValues;
                const next = emptyFactorFormBase();
                next.factorIdx = full?.factorIdx ?? factorIdx;
                next.factorCode = full?.factorCode ?? this.factorToEdit?.factorCode ?? "";
                next.factorName = full?.factorName ?? "";
                next.country = full?.country ?? "KR";
                next.fuelType = full?.fuelType ?? "";
                next.factorType = full?.factorType || "FUEL";
                next.draftStat = full?.draftStat || "DRAFT";
                next.description = full?.remarks ?? full?.description ?? "";

                if (Array.isArray(values) && values.length > 0) {
                    const current = values.find((v) => v && (v.endMonth == null || v.endMonth === "")) || values[0];
                    const cal = getCalorificFromComponents(current);
                    const basis = current?.detailBasis || {};
                    const factorType = full?.factorType || next.factorType || "FUEL";
                    const split = splitSpeciesRowsFromComponents(current?.components);
                    const currentGasUnit =
                        current?.components?.find?.((c) => {
                            const code = String(c?.componentCode || "").trim();
                            if (code === "NCV" || code === "GCV") return false;
                            return true;
                        })?.emissionUnit || (String(factorType).toUpperCase() === "ACTIVITY" ? "kgGHG/kg" : "kgGHG/TJ");

                    next.valueIdx = current?.valueIdx ?? null;
                    next.startMonth = current?.startMonth ?? "";
                    next.endMonth = current?.endMonth ?? "";
                    next.source = current?.source ?? "";
                    next.CO2Factor = split.CO2Factor != null ? split.CO2Factor : null;
                    next.CH4Factor = split.CH4Factor;
                    next.N2OFactor = split.N2OFactor;
                    next.SF6Factor = split.SF6Factor;
                    next.hfcSpeciesRows = split.hfcSpeciesRows;
                    next.pfcSpeciesRows = split.pfcSpeciesRows;
                    next.gasEmissionUnit = currentGasUnit;
                    next.ncvValue = cal.ncv;
                    next.ncvUnit = cal.ncvUnit || "";
                    next.gcvValue = cal.gcv;
                    next.gcvUnit = cal.gcvUnit || "";
                    next.detailBasisHFCs = basis?.HFCsFactor?.summary ?? "";
                    next.detailBasisPFCs = basis?.PFCsFactor?.summary ?? "";
                }
                this.form = next;
                this.valueFormKey += 1;
            } catch (err) {
                console.error("[FactorFormModal] 로드 실패:", err);
                this.$bvToast.toast("배출계수 정보를 불러오는데 실패했습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
            } finally {
                this.loading = false;
            }
        },
        appendDetailBasisIfAny(valuePayload, hfcs, pfcs) {
            const db = {};
            if (hfcs > 0) {
                const s = String(this.form.detailBasisHFCs || "").trim();
                if (s) db.HFCsFactor = { summary: s };
            }
            if (pfcs > 0) {
                const s = String(this.form.detailBasisPFCs || "").trim();
                if (s) db.PFCsFactor = { summary: s };
            }
            if (Object.keys(db).length) {
                valuePayload.detailBasis = db;
            }
        },
        async submit() {
            const { hfcs, pfcs } = basketPositiveTotals(this.form.hfcSpeciesRows, this.form.pfcSpeciesRows);

            const components = buildAllComponents(this.form, {
                gasEmissionUnit: this.form.gasEmissionUnit,
                hfcSpeciesRows: this.form.hfcSpeciesRows,
                pfcSpeciesRows: this.form.pfcSpeciesRows,
                ncvValue: this.form.ncvValue,
                ncvUnit: this.form.ncvUnit,
                gcvValue: this.form.gcvValue,
                gcvUnit: this.form.gcvUnit,
            });
            const uniq = validateUniqueGasComponentCodes(components);
            if (!uniq.ok) {
                this.$bvToast.toast(`가스 componentCode가 중복입니다: ${uniq.duplicate}`, {
                    title: "입력 오류",
                    variant: "danger",
                    solid: true,
                });
                return;
            }

            this.saving = true;
            try {
                if (this.isCreate) {
                    const metaPayload = {
                        factorCode: this.autoGeneratedFactorCode,
                        factorName: this.form.factorName ? String(this.form.factorName).trim() : null,
                        country: this.form.country,
                        fuelType: this.form.fuelType,
                        factorType: this.form.factorType || "FUEL",
                        remarks: this.form.description,
                        draftStat: this.form.draftStat || "DRAFT",
                    };
                    const createRes = await carbonApi.emissionFactor.createFactor(metaPayload);
                    const created = createRes.data?.data ?? createRes.data;
                    const factorIdx = created?.factorIdx;
                    if (factorIdx == null) {
                        throw new Error("배출계수 생성 후 factorIdx를 받지 못했습니다.");
                    }

                    const valuePayload = {
                        startMonth: this.form.startMonth || this._currentYYYYMM(),
                        endMonth: this.form.endMonth || null,
                        source: this.form.source || null,
                        remarks: this.form.description,
                        components,
                    };
                    this.appendDetailBasisIfAny(valuePayload, hfcs, pfcs);
                    await carbonApi.emissionFactor.addFactorValue(factorIdx, valuePayload);

                    this.$bvToast.toast(`배출계수 "${this.autoGeneratedFactorCode}"가 추가되었습니다.`, {
                        title: "성공",
                        variant: "success",
                        solid: true,
                    });
                    this.innerVisible = false;
                    this.$emit("success", { created: true });
                } else {
                    const factorData = {
                        factorName: this.form.factorName ? String(this.form.factorName).trim() : null,
                        country: this.form.country,
                        fuelType: this.form.fuelType,
                        factorType: this.form.factorType || "FUEL",
                        remarks: this.form.description,
                        draftStat: this.form.draftStat || "DRAFT",
                    };
                    const response = await carbonApi.emissionFactor.updateFactor(this.form.factorIdx, factorData);

                    const valueIdx = this.form.valueIdx;
                    if (valueIdx != null) {
                        const valuePayload = {
                            endMonth: this.form.endMonth || null,
                            source: this.form.source || null,
                            remarks: this.form.description,
                            components,
                        };
                        this.appendDetailBasisIfAny(valuePayload, hfcs, pfcs);
                        await carbonApi.emissionFactor.updateFactorValue(this.form.factorIdx, valueIdx, valuePayload);
                    }

                    if (response.data) {
                        this.$bvToast.toast(`배출계수 "${this.form.factorCode}"가 수정되었습니다.`, {
                            title: "성공",
                            variant: "success",
                            solid: true,
                        });
                        this.innerVisible = false;
                        this.$emit("success", { created: false });
                    } else {
                        throw new Error("배출계수 수정 실패");
                    }
                }
            } catch (error) {
                if (this.isCreate) {
                    console.error("[FactorFormModal] 배출계수 추가 실패:", error);
                    const msg = error.response?.data?.message ?? error.message ?? "배출계수 추가에 실패했습니다.";
                    const status = error.response?.status;
                    const isDuplicate = status === 409 || /중복|이미\s*존재|unique|duplicate|uk_factor_code/i.test(msg);
                    const toastMsg = isDuplicate
                        ? `"${this.autoGeneratedFactorCode}"는 이미 사용 중인 배출계수 코드입니다. 국가/연료/시작월을 변경해주세요.`
                        : msg;
                    this.$bvToast.toast(toastMsg, {
                        title: isDuplicate ? "코드 중복" : "오류",
                        variant: "danger",
                        solid: true,
                    });
                } else {
                    console.error("[FactorFormModal] 수정 실패:", error);
                    const errorMessage =
                        error.response?.data?.message || error.message || "배출계수 수정에 실패했습니다.";
                    this.$bvToast.toast(errorMessage, {
                        title: "오류",
                        variant: "danger",
                        solid: true,
                    });
                }
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
:deep(.factor-modal-shell .modal-dialog.factor-modal-dialog) {
    max-width: 620px;
}

:deep(.factor-modal-content) {
    border: 1px solid #d7e0e8;
    border-radius: 16px;
    overflow: hidden;
    box-shadow:
        0 18px 42px rgba(15, 23, 42, 0.12),
        0 2px 12px rgba(15, 23, 42, 0.06);
}

:deep(.factor-modal-header) {
    padding: 14px 18px 12px;
    border-bottom: 1px solid #e3e9ef;
    background: linear-gradient(180deg, #fbfcfd 0%, #f6f9fc 100%);
}

:deep(.factor-modal-header .modal-title) {
    font-size: 15px;
    font-weight: 700;
    color: #132238;
    letter-spacing: -0.01em;
}

:deep(.factor-modal-header .close) {
    color: #7b8da2;
    opacity: 1;
    text-shadow: none;
}

:deep(.factor-modal-body) {
    padding: 14px 16px 10px;
    background: linear-gradient(180deg, #f4f8fb 0%, #f8fafc 100%);
}

:deep(.factor-modal-footer) {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px 14px;
    border-top: 1px solid #e3e9ef;
    background: #fbfcfd;
}

:deep(.factor-modal-footer .btn) {
    min-width: 88px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 600;
}

:deep(.factor-modal-footer .btn-secondary) {
    color: #334155;
    background: #f8fafc;
    border-color: #d8e1ea;
}

:deep(.factor-modal-footer .btn-primary) {
    background: #0f766e;
    border-color: #0f766e;
    box-shadow: none;
}

.factor-modal-form {
    max-height: 70vh;
    overflow-y: auto;
    padding: 2px;
}

.factor-modal-form::-webkit-scrollbar {
    width: 8px;
}

.factor-modal-form::-webkit-scrollbar-track {
    background: #edf2f7;
    border-radius: 99px;
}

.factor-modal-form::-webkit-scrollbar-thumb {
    background: #c2ceda;
    border-radius: 99px;
}

.factor-modal-form::-webkit-scrollbar-thumb:hover {
    background: #aebdcc;
}

.registration-unified {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
    max-width: 860px;
    margin-left: auto;
    margin-right: auto;
}

.registration-form .row {
    margin-left: -6px;
    margin-right: -6px;
}

.registration-form .row > [class*="col-"] {
    padding-left: 6px;
    padding-right: 6px;
}

.form-section {
    border: 0;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.84);
    padding: 11px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.form-section-basic {
    background: linear-gradient(180deg, rgba(252, 254, 255, 0.92) 0%, rgba(247, 251, 254, 0.92) 100%);
}

.form-section-calorific {
    background: linear-gradient(180deg, rgba(251, 253, 255, 0.86) 0%, rgba(246, 250, 253, 0.86) 100%);
}

.form-section-remarks {
    background: rgba(250, 252, 255, 0.86);
}

.section-header {
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.section-header h6 {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.01em;
    color: #2d4256;
}

.section-pill {
    width: 3px;
    height: 13px;
    border-radius: 999px;
    background: #0f766e;
}

.section-pill-gray {
    background: #8fa3b8;
}

.section-brief {
    margin: 0 0 9px;
    font-size: 10px;
    line-height: 1.35;
    color: #71879b;
}

.optional-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: 999px;
    border: 0;
    background: #edf3f9;
    color: #5e778d;
    font-size: 9px;
    font-weight: 700;
}

.optional-chip--section {
    margin-left: auto;
}

.form-group {
    margin-bottom: 8px;
}

.form-group label {
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 600;
    color: #4f6579;
    letter-spacing: -0.01em;
}

.form-section-basic .col-md-12 .form-group {
    max-width: 560px;
}

.form-section-basic .col-md-6 .form-group {
    max-width: 330px;
}

.registration-form .form-control {
    height: 31px;
    border: 1px solid #d9e3ec;
    border-radius: 8px;
    box-shadow: none;
    font-size: 11px;
    color: #1f2937;
    background: rgba(255, 255, 255, 0.94);
    transition:
        border-color 0.16s ease,
        box-shadow 0.16s ease,
        background-color 0.16s ease;
}

.registration-form .form-control:hover {
    border-color: #bfd0de;
}

.registration-form .form-control:focus {
    border-color: #4d84ac;
    box-shadow: 0 0 0 2px rgba(77, 132, 172, 0.14);
    outline: none;
}

.registration-form textarea.form-control {
    height: auto;
    min-height: 68px;
    resize: vertical;
    padding-top: 8px;
    padding-bottom: 8px;
    max-width: 760px;
}

.calorific-intro {
    margin-bottom: 8px;
    font-size: 10px;
    line-height: 1.35;
    color: #6f8599;
}

.calorific-cols {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    max-width: 720px;
}

.calorific-col {
    min-width: 0;
    max-width: 350px;
}

.calorific-label {
    display: block;
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 600;
    color: #50667b;
}

.calorific-input-group {
    display: flex;
    align-items: stretch;
    gap: 6px;
    max-width: 330px;
}

.calorific-input-group > .form-control:first-child {
    flex: 1 1 auto;
}

.calorific-unit-select {
    flex: 0 0 42%;
    max-width: 42%;
}

.registration-code-meta {
    margin-top: 6px;
    padding: 9px 11px;
    border: 0;
    border-radius: 10px;
    background: rgba(241, 246, 251, 0.82);
    max-width: 860px;
    margin-left: auto;
    margin-right: auto;
}

.footer-factorcode {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 11px;
}

.footer-factorcode-label {
    color: #5f748a;
    font-weight: 600;
}

.footer-factorcode-value {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 10px;
    font-weight: 600;
    color: #1f3347;
    letter-spacing: 0.02em;
    padding: 2px 6px;
    border: 0;
    border-radius: 6px;
    background: #ffffff;
}

.footer-factorcode-status {
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
}

.footer-factorcode-status--available {
    color: #0f766e;
}

.footer-factorcode-badge {
    display: inline-block;
    padding: 2px 7px;
    font-size: 9px;
    font-weight: 700;
    border-radius: 999px;
}

.footer-factorcode-badge--duplicate {
    color: #a63d2f;
    background: #fdf2f2;
    border: 1px solid #f7c7c3;
}

.footer-factorcode-status--checking {
    color: #b9770e;
}

.required {
    color: #c0372c;
}

@media (max-width: 991px) {
    .calorific-cols {
        grid-template-columns: 1fr;
    }

    .form-section-basic .col-md-12 .form-group,
    .form-section-basic .col-md-6 .form-group,
    .calorific-col,
    .calorific-input-group {
        max-width: 100%;
    }
}

@media (max-width: 767px) {
    :deep(.factor-modal-shell .modal-dialog.factor-modal-dialog) {
        margin: 0.5rem;
    }

    :deep(.factor-modal-body) {
        padding: 10px 10px 8px;
    }

    .form-section {
        padding: 10px;
    }

    .registration-form .row > [class*="col-"] {
        padding-left: 5px;
        padding-right: 5px;
    }
}
</style>
